// Recording the mic onto the room's timeline.
//
// Press record: an optional count-in, then the transport rolls from the
// playhead and the mic runs. Press it again — or stop the transport, as Live
// does — and the take is cut to what lines up with what was heard
// (lib/recording.ts `takeWindow`), encoded, stored (useTakeStore), and lands as
// a clip on the target lane where it was played. One proposal, so it undoes
// as one.
//
// Count-in and the input-latency offset are per browser: a listening setup,
// not room state, so they live in prefs and never on the wire.

import { useCallback, useEffect, useRef, useState } from "react";
import type { CanonicalRoom, CanonicalTrackState } from "@shared/sync/state.js";
import type { Intent } from "@shared/sync/intents.js";
import type { ArrangementPlayerApi } from "@web-shared/hooks/useArrangementPlayer";
import { isFiniteNumber, usePref } from "@web-shared/hooks/usePref";
import { createAudioClip } from "@web-shared/lib/edits";
import { firstFreeSlot } from "@web-shared/lib/placement";
import { encodeWav24 } from "@web-shared/lib/wav";
import { formatTime } from "@web-shared/lib/audio-engine";
import { recordTarget, takeWindow } from "@/lib/recording";
import { cleanTrackName } from "@/lib/track-name";
import type { TakeStore } from "./useTakeStore";

export interface RoomRecorderArgs {
  room: CanonicalRoom | null;
  player: ArrangementPlayerApi;
  tempo: number;
  editor: { propose: (intents: Intent[]) => unknown };
  store: TakeStore;
  /** The lane the producer has selected — the target when nothing is armed. */
  selectedLaneId: string | null;
}

export interface RoomRecorder {
  /** Where a take would land now; null when there is nowhere. */
  target: CanonicalTrackState | null;
  /** Mic asked for or counting in — the take hasn't started yet. */
  arming: boolean;
  recording: boolean;
  /** Uploading or placing the last take. */
  saving: boolean;
  /** One line about the last take that didn't go as pressed. */
  note: string | null;
  dismissNote: () => void;
  toggle: () => void;
  countIn: number;
  setCountIn: (bars: number) => void;
  offsetMs: number;
  setOffsetMs: (ms: number) => void;
  /** Loopback-measure the offset and keep it; resolves to what to say. */
  calibrate: () => Promise<string>;
}

export function useRoomRecorder({ room, player, tempo, editor, store, selectedLaneId }: RoomRecorderArgs): RoomRecorder {
  const [countIn, setCountIn] = usePref("seedbed-web.record.countIn", 0, isFiniteNumber);
  const [offsetMs, setOffsetMs] = usePref("seedbed-web.record.offsetMs", 0, isFiniteNumber);
  const [arming, setArming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  // A take lands on the room as it is when it STOPS — collaborators may have
  // edited it while the mic ran.
  const roomRef = useRef(room);
  roomRef.current = room;
  const target = room ? recordTarget(room, selectedLaneId) : null;
  // The lane is fixed when the take starts: re-arming mid-take must not move it.
  const takeLane = useRef<string | null>(null);
  const armingRef = useRef(false);

  const start = useCallback(() => {
    if (armingRef.current || player.recording) return;
    if (!target) {
      setNote("Arm an audio lane (●), or select one, to record onto it.");
      return;
    }
    armingRef.current = true;
    setArming(true);
    setNote(null);
    takeLane.current = target.id;
    player.stopAudition();
    player
      .startRecording(countIn)
      .catch((e: unknown) => {
        takeLane.current = null;
        setNote(`Could not record: ${e instanceof Error ? e.message : String(e)}`);
      })
      .finally(() => {
        armingRef.current = false;
        setArming(false);
      });
  }, [player, target, countIn]);

  // The button and the transport stopping can both end the same take.
  const finishing = useRef(false);
  const finish = useCallback(async () => {
    if (finishing.current) return;
    finishing.current = true;
    const laneId = takeLane.current;
    takeLane.current = null;
    // Where the transport wrapped while recording (ArrangementPlayer.tick): a
    // loop region always wraps; the whole Set only with loop on.
    const range = player.loopRange;
    const loopEndSec = range && range.endSec > range.startSec ? range.endSec : player.loop ? player.duration : null;
    const take = await player.stopRecording().finally(() => {
      finishing.current = false;
    });
    if (!take) {
      setNote("Nothing was captured.");
      return;
    }
    const current = roomRef.current;
    const lane = current?.tracks.find((t) => t.id === laneId);
    if (!current || !lane) {
      setNote("The lane the take was for is gone.");
      return;
    }
    const w = takeWindow(take.startSec, take.buffer.duration, offsetMs / 1000, loopEndSec);
    if (!w) {
      setNote("Nothing was captured.");
      return;
    }
    setSaving(true);
    try {
      const key = await store.put(encodeWav24(take.buffer, w.fromSec, w.toSec));
      const after = roomRef.current ?? current;
      const atBeat = (w.atSec * tempo) / 60;
      const n = after.clips.filter((c) => c.trackId === lane.id).length + 1;
      let landed = atBeat;
      const intent = createAudioClip(
        after,
        lane.id,
        atBeat,
        ((w.toSec - w.fromSec) * tempo) / 60,
        key,
        `${cleanTrackName(lane.name)} take ${n}`,
        (s, d) => {
          const at = firstFreeSlot(after, lane.id, s, d);
          if (at !== null) landed = at;
          return at;
        },
      );
      if (!intent) throw new Error("no room on the lane for it.");
      editor.propose([intent]);
      // The lane already had a clip where the take was played. Live would cut
      // it; the room doesn't overwrite, so it says where the take went instead.
      setNote(
        Math.abs(landed - atBeat) > 1e-6
          ? `${cleanTrackName(lane.name)} was busy there — the take is at ${formatTime((landed * 60) / tempo)}.`
          : null,
      );
    } catch (e: unknown) {
      setNote(`Could not place the take: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  }, [player, store, tempo, editor, offsetMs]);

  const toggle = useCallback(() => {
    if (player.recording) void finish();
    else start();
  }, [player.recording, finish, start]);

  // Stopping the transport ends the take, as it does in Live: the recorder
  // doesn't follow the playhead, so anything captured after it stopped (or
  // jumped) would land somewhere it wasn't played.
  const wasPlaying = useRef(false);
  const playing = player.status === "playing";
  useEffect(() => {
    if (wasPlaying.current && !playing && player.recording) void finish();
    wasPlaying.current = playing;
  }, [playing, player.recording, finish]);

  const calibrate = useCallback(async (): Promise<string> => {
    try {
      const est = await player.calibrateLatency();
      if (!est) return "Nothing heard — is the mic allowed and the volume up?";
      if (est.confidence < 3) return `Unsure (${Math.round(est.offsetMs)} ms) — try a quieter room, louder speakers.`;
      setOffsetMs(Math.round(est.offsetMs));
      return `Measured ${Math.round(est.offsetMs)} ms.`;
    } catch (e: unknown) {
      return e instanceof Error ? e.message : String(e);
    }
  }, [player, setOffsetMs]);

  return {
    target,
    arming,
    recording: player.recording,
    saving,
    note,
    dismissNote: useCallback(() => setNote(null), []),
    toggle,
    countIn,
    setCountIn,
    offsetMs,
    setOffsetMs,
    calibrate,
  };
}
